import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  useSelfAllocation,
  useSelfAllocationAvailableProjects,
  useSelfAllocationAvailableProjectGroups,
  useSubmitSelfAllocation,
  useUpdateSelfAllocation,
  useDeleteSelfAllocation,
} from "@/features/finances/hooks/useSelfAllocation";
import {
  SelfAllocationGridEditor,
  fractionsAreGridFriendly,
} from "@/features/finances/components/SelfAllocationGridEditor";
import { usePageTitle } from "@/hooks/usePageTitle";

type Tab = "form" | "grid";

const MONTHS_PL = [
  "styczeń",
  "luty",
  "marzec",
  "kwiecień",
  "maj",
  "czerwiec",
  "lipiec",
  "sierpień",
  "wrzesień",
  "październik",
  "listopad",
  "grudzień",
];

function formatPeriod(year: number, month: number) {
  return `${MONTHS_PL[month - 1]} ${year}`;
}

function pillForStatus(status: string) {
  switch (status) {
    case "draft":
      return { label: "Szkic", klass: "bg-gray-100 text-gray-700" };
    case "submitted":
      return { label: "Złożona", klass: "bg-blue-100 text-blue-800" };
    case "approved":
      return { label: "Zatwierdzona", klass: "bg-green-100 text-green-800" };
    case "rejected":
      return { label: "Odrzucona", klass: "bg-red-100 text-red-800" };
    default:
      return { label: status, klass: "bg-gray-100 text-gray-700" };
  }
}

export default function SelfAllocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: req, isLoading } = useSelfAllocation(id);
  usePageTitle(req && `Samo-alokacja: ${formatPeriod(req.year, req.month)}`);
  // Nowe wnioski rozliczają się na grupy przedsięwzięć; historyczne — na
  // pojedyncze przedsięwzięcia. Selektor jednostek dobieramy do trybu wniosku.
  const mode = req?.allocation_unit === "group" ? "group" : "project";
  const { data: projectUnits = [] } = useSelfAllocationAvailableProjects(
    id,
    !!req && mode === "project",
  );
  const { data: groupUnits = [] } = useSelfAllocationAvailableProjectGroups(
    id,
    !!req && mode === "group",
  );
  const units = mode === "group" ? groupUnits : projectUnits;
  const unitHeader = mode === "group" ? "Grupa przedsięwzięć" : "Projekt";
  const updateMut = useUpdateSelfAllocation(id || "");
  const submitMut = useSubmitSelfAllocation();
  const deleteMut = useDeleteSelfAllocation();

  // Local state: map of project id → fraction string
  const [fractions, setFractions] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("form");

  // Hydrate local state from server data once.
  // Also pick the default tab based on what's already in the request:
  // - empty (no lines) → graphical, since the user will likely start clicking tiles
  // - all fractions are multiples of 10% → graphical (lossless representation)
  // - otherwise → form (grid can't represent precision smaller than 10%)
  useEffect(() => {
    if (!req) return;
    const f: Record<string, string> = {};
    // Convert dot-separated server values to comma (Polish locale). A line keys
    // by its group (nowe wnioski) or its project (historyczne).
    for (const line of req.lines) {
      const key = line.project_group ?? line.project;
      if (key) f[key] = line.fraction.replace(".", ",");
    }
    setFractions(f);
    setNotes(req.notes);
    if (req.lines.length === 0 || fractionsAreGridFriendly(req.lines)) {
      setTab("grid");
    } else {
      setTab("form");
    }
  }, [req?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = req?.status === "draft" || req?.status === "rejected";
  const parseVal = (v: string) =>
    parseFloat(v.replace(",", ".")) || 0;

  const total = useMemo(
    () => Object.values(fractions).reduce((acc, v) => acc + parseVal(v), 0),
    [fractions],
  );
  const sumOk = Math.abs(total - 1) <= 0.001;
  const gridFriendly =
    Object.keys(fractions).length === 0 || fractionsAreGridFriendly(fractions);

  useEffect(() => {
    if (!gridFriendly && tab === "grid") setTab("form");
  }, [gridFriendly, tab]);

  if (isLoading || !req) return <PageLoader />;

  // Drafts are editable all month, but a request may only be SUBMITTED from the
  // last day of its month onward — the backend is authoritative (`submittable`).
  const periodSubmittable = req.submittable;
  const submittableFromLabel = (() => {
    const [y, m, d] = (req.submittable_from || "").split("-");
    return d && m && y ? `${d}.${m}.${y}` : "";
  })();

  const handleFractionChange = (pid: string, val: string) => {
    setFractions((prev) => {
      const next = { ...prev };
      if (val === "") delete next[pid];
      else next[pid] = val;
      return next;
    });
  };

  const handleFractionKeyDown = (
    pid: string,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const current = parseVal(fractions[pid] ?? "0");
    const delta = e.key === "ArrowUp" ? 0.01 : -0.01;
    const next = Math.max(0, Math.min(1, current + delta));
    handleFractionChange(pid, next.toFixed(2).replace(".", ","));
  };

  const buildLines = () =>
    Object.entries(fractions)
      .filter(([, v]) => parseVal(v) > 0)
      .map(([unitId, fraction]) => ({
        ...(mode === "group"
          ? { project_group: unitId }
          : { project: unitId }),
        fraction: fraction.replace(",", "."),
      }));

  const handleSave = async () => {
    setErr(null);
    try {
      await updateMut.mutateAsync({ notes, lines: buildLines() });
    } catch (e: any) {
      setErr(e?.response?.data?.errors?.detail ?? "Błąd zapisu.");
    }
  };

  const handleSubmit = async () => {
    setErr(null);
    if (!periodSubmittable) {
      setErr(
        `Wniosek można złożyć dopiero od ${submittableFromLabel} (ostatni dzień miesiąca). Do tego czasu zapisuj szkic.`,
      );
      return;
    }
    if (!sumOk) {
      setErr("Suma ułamków musi wynosić 100% przed wysłaniem.");
      return;
    }
    try {
      // Save first to flush local edits, then submit.
      await updateMut.mutateAsync({ notes, lines: buildLines() });
      await submitMut.mutateAsync(req.id);
    } catch (e: any) {
      setErr(e?.response?.data?.errors?.detail ?? "Błąd wysłania.");
    }
  };

  const handleDelete = async () => {
    if (!confirm("Usunąć ten wniosek?")) return;
    await deleteMut.mutateAsync(req.id);
    navigate("/finances/self-allocation");
  };

  const pill = pillForStatus(req.status);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-2">
        <h1 className="text-2xl font-semibold">
          Rozkład pracy: {formatPeriod(req.year, req.month)}
        </h1>
        <span
          className={`text-xs px-2 py-1 rounded ${pill.klass}`}
          data-testid="status-pill"
        >
          {pill.label}
        </span>
      </div>

      {req.status === "rejected" && req.rejection_reason && (
        <div className="border border-red-200 bg-red-50 rounded p-3 mb-4 text-sm">
          <strong className="text-red-800">Powód odrzucenia:</strong>{" "}
          <span className="text-red-900">{req.rejection_reason}</span>
          <p className="text-xs text-red-700 mt-1">
            Po edycji wniosek wróci do stanu „Szkic" i będzie można go ponownie
            wysłać.
          </p>
        </div>
      )}

      {req.status === "approved" && (
        <div className="border border-green-200 bg-green-50 rounded p-3 mb-4 text-sm text-green-900">
          Wniosek zatwierdzony
          {req.decided_by_name ? ` przez ${req.decided_by_name}` : ""}. Alokacje
          zostały zaktualizowane.
        </div>
      )}

      {/* Tabs: Formularz / Graficzny */}
      <div className="flex gap-1 border-b border-gray-200 mb-3">
        <button
          type="button"
          onClick={() => setTab("form")}
          data-testid="tab-form"
          className={`px-3 py-2 text-sm border-b-2 ${
            tab === "form"
              ? "border-blue-600 text-blue-700 font-medium"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Formularz
        </button>
        {gridFriendly && (
          <button
            type="button"
            onClick={() => setTab("grid")}
            data-testid="tab-grid"
            className={`px-3 py-2 text-sm border-b-2 ${
              tab === "grid"
                ? "border-blue-600 text-blue-700 font-medium"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Graficzny (×10%)
          </button>
        )}
      </div>


      {tab === "grid" && (
        <div className="bg-white border border-gray-200 rounded mb-4 p-4">
          <SelfAllocationGridEditor
            projects={units}
            mode={mode}
            value={fractions}
            disabled={!editable}
            onChange={(next) => setFractions(next)}
          />
        </div>
      )}

      {tab === "form" && (
        <div className="bg-white border border-gray-200 rounded mb-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500">
                  {unitHeader}
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 w-40">
                  Ułamek czasu pracy (0–1)
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 w-20">
                  %
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 w-32">
                  Dyrektor ustawił
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {units.map((p) => {
                const val = fractions[p.id] ?? "";
                const dir = parseFloat(p.existing_director_fraction || "0");
                return (
                  <tr key={p.id}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{p.short_name}</div>
                      <div className="text-xs text-gray-500">{p.name}</div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <input
                          type="text"
                          inputMode="decimal"
                          disabled={!editable}
                          value={val}
                          onChange={(e) =>
                            handleFractionChange(p.id, e.target.value)
                          }
                          onKeyDown={(e) => handleFractionKeyDown(p.id, e)}
                          data-testid={`fraction-${p.short_name}`}
                          className="w-20 border border-gray-300 rounded px-2 py-1 text-right font-mono"
                          placeholder="0,00"
                        />
                        {editable && (
                          <div className="flex flex-col">
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() =>
                                handleFractionKeyDown(p.id, {
                                  key: "ArrowUp",
                                  preventDefault: () => {},
                                } as React.KeyboardEvent<HTMLInputElement>)
                              }
                              className="h-4 w-5 flex items-center justify-center border border-gray-300 rounded-t bg-gray-50 hover:bg-gray-100 text-gray-600 text-[10px] leading-none"
                            >
                              ▲
                            </button>
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() =>
                                handleFractionKeyDown(p.id, {
                                  key: "ArrowDown",
                                  preventDefault: () => {},
                                } as React.KeyboardEvent<HTMLInputElement>)
                              }
                              className="h-4 w-5 flex items-center justify-center border border-t-0 border-gray-300 rounded-b bg-gray-50 hover:bg-gray-100 text-gray-600 text-[10px] leading-none"
                            >
                              ▼
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600 font-mono">
                      {val ? `${Math.round(parseVal(val) * 100)}%` : "—"}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-gray-500">
                      {dir > 0 ? `${Math.round(dir * 100)}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td className="px-4 py-2 font-medium">Suma</td>
                <td className="px-4 py-2 text-right font-mono">
                  <span
                    data-testid="lines-total"
                    className={sumOk ? "text-green-700" : "text-red-700"}
                  >
                    {total.toFixed(3)}
                  </span>
                </td>
                <td className="px-4 py-2 text-right font-mono">
                  <span className={sumOk ? "text-green-700" : "text-red-700"}>
                    {Math.round(total * 100)}%
                  </span>
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Always-visible summary chip (independent of tab) */}
      <div className="text-xs text-gray-500 mb-3" data-testid="summary-chip">
        Suma:{" "}
        <strong className={sumOk ? "text-green-700" : "text-red-700"}>
          {Math.round(total * 100)}%
        </strong>
        {!sumOk && " — przed wysłaniem musi być 100%."}
      </div>

      <div className="mb-4">
        <label className="text-xs uppercase tracking-widest text-gray-500 block mb-1">
          Uwagi
        </label>
        <textarea
          rows={2}
          disabled={!editable}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="notes"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="Opcjonalny komentarz"
        />
      </div>

      {editable && !periodSubmittable && (
        <div
          className="border border-amber-200 bg-amber-50 text-amber-800 rounded px-3 py-2 mb-3 text-sm"
          data-testid="submit-locked-hint"
        >
          To bieżący miesiąc — uzupełniaj szkic na bieżąco. Złożenie do akceptacji
          będzie możliwe od <strong>{submittableFromLabel}</strong> (ostatni dzień
          miesiąca).
        </div>
      )}

      {err && (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded px-3 py-2 mb-3 text-sm">
          {err}
        </div>
      )}

      <div className="flex items-center gap-2">
        {editable && (
          <>
            <Button
              variant="secondary"
              onClick={handleSave}
              loading={updateMut.isPending}
              data-testid="btn-save"
            >
              Zapisz szkic
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!sumOk || !periodSubmittable}
              loading={submitMut.isPending}
              title={
                !periodSubmittable
                  ? `Złożysz od ${submittableFromLabel} (ostatni dzień miesiąca)`
                  : !sumOk
                    ? "Suma musi wynosić 100%"
                    : ""
              }
              data-testid="btn-submit"
            >
              Wyślij do akceptacji
            </Button>
            <Button
              variant="ghost"
              onClick={handleDelete}
              loading={deleteMut.isPending}
              data-testid="btn-delete"
            >
              Usuń
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          onClick={() => navigate("/finances/self-allocation")}
        >
          Wróć do listy
        </Button>
      </div>
    </div>
  );
}
